// Sender accounts admin page.
//
// CRUD over outreach_sender_accounts rows. Each row is one outbound
// identity the send worker will rotate through, with its own daily_cap
// and on/off switch. WA rows additionally carry an evolution_instance.
//
// One-time "Add the TG bridge" quick-action pre-fills the known TG
// outreach userbot (account id 8572914098, phone +6581161183) to save
// the user from typing.

import { useEffect, useState } from 'react';
import { Plus, Trash2, Sparkles, MessageSquare, Phone } from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { useToast } from '../lib/toast';
import { fmtRelative } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

type Channel = 'wa' | 'tg';

interface SenderAccount {
  id: string;
  channel: Channel;
  identifier: string;
  display_name: string | null;
  daily_cap: number;
  enabled: boolean;
  evolution_instance: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Pre-filled defaults for the user's known TG outreach userbot.
// (Account "GoDrop", id 8572914098, on +6581161183, set up 2026-05-12.)
const TG_QUICK_ADD = {
  channel: 'tg' as Channel,
  identifier: '8572914098',
  display_name: 'GoDrop outreach +6581161183',
  daily_cap: 15,
  enabled: true,
};

export function SenderAccountsPage() {
  const [rows, setRows] = useState<SenderAccount[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [prefill, setPrefill] = useState<Partial<typeof TG_QUICK_ADD> | null>(null);
  const { show: toast } = useToast();

  function load() {
    setRows(null);
    setErr(null);
    api<{ accounts: SenderAccount[] }>('/api/admin/outreach/sender-accounts')
      .then((r) => setRows(r.accounts))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }
  useEffect(load, []);

  async function setField(id: string, body: Partial<SenderAccount>) {
    setBusyId(id);
    try {
      await api(`/api/admin/outreach/sender-accounts/${id}`, {
        method: 'PATCH',
        json: body,
      });
      load();
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Update failed', 'error');
      load();
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this sender account?')) return;
    setBusyId(id);
    try {
      await api(`/api/admin/outreach/sender-accounts/${id}`, { method: 'DELETE' });
      toast('Deleted', 'success');
      load();
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Delete failed', 'error');
    } finally {
      setBusyId(null);
    }
  }

  function openAdd(pre?: Partial<typeof TG_QUICK_ADD>) {
    setPrefill(pre ?? null);
    setAddOpen(true);
  }

  // Hide the TG quick-add button if the user already added a TG sender
  // matching the known identifier.
  const hasTgQuickAdd = (rows ?? []).some(
    (r) => r.channel === 'tg' && r.identifier === TG_QUICK_ADD.identifier,
  );

  return (
    <Shell title="Sender accounts">
      <PageHeader
        title="Sender accounts"
        description="Outbound identities the send worker rotates through. Each has its own daily cap and on/off switch."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {!hasTgQuickAdd && (
              <Button variant="secondary" onClick={() => openAdd(TG_QUICK_ADD)}>
                <Sparkles className="h-4 w-4" /> Quick-add TG bridge
              </Button>
            )}
            <Button onClick={() => openAdd()}>
              <Plus className="h-4 w-4" /> Add sender
            </Button>
          </div>
        }
      />

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {!rows &&
          Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}

        {rows && rows.length === 0 && (
          <Card>
            <CardContent>
              <EmptyState
                icon={Phone}
                title="No sender accounts yet."
                description="Add one to enable outbound sending. Approved drafts queue up until at least one sender is enabled."
              />
            </CardContent>
          </Card>
        )}

        {rows?.map((r) => {
          const ChannelIcon = r.channel === 'tg' ? MessageSquare : Phone;
          return (
            <Card key={r.id}>
              <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={r.channel === 'tg' ? 'brand' : 'accepted'}>
                      <ChannelIcon className="h-3 w-3" /> {r.channel.toUpperCase()}
                    </Badge>
                    <Badge variant={r.enabled ? 'completed' : 'cancelled'}>
                      {r.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <span className="font-semibold text-text">
                      {r.display_name ?? r.identifier}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-text-muted">
                    id: <code className="rounded bg-surface-muted px-1.5 py-0.5">{r.identifier}</code>
                    {' · '}
                    cap: <code className="rounded bg-surface-muted px-1.5 py-0.5">{r.daily_cap}/day</code>
                    {r.evolution_instance && (
                      <>
                        {' · evolution: '}
                        <code className="rounded bg-surface-muted px-1.5 py-0.5">{r.evolution_instance}</code>
                      </>
                    )}
                    {' · added '}
                    {fmtRelative(r.created_at)}
                  </p>
                  {r.notes && (
                    <p className="mt-1 text-xs italic text-text-subtle">{r.notes}</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 md:flex-col md:items-end">
                  <Button
                    variant={r.enabled ? 'ghost' : 'primary'}
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => setField(r.id, { enabled: !r.enabled })}
                  >
                    {r.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <DailyCapEditor
                    current={r.daily_cap}
                    disabled={busyId === r.id}
                    onSave={(n) => setField(r.id, { daily_cap: n })}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-error"
                    disabled={busyId === r.id}
                    onClick={() => remove(r.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AddSenderDialog
        open={addOpen}
        prefill={prefill}
        onClose={() => { setAddOpen(false); setPrefill(null); }}
        onSaved={load}
      />
    </Shell>
  );
}

// ---------- Inline daily-cap editor ---------------------------------------

interface CapProps {
  current: number;
  disabled: boolean;
  onSave: (n: number) => void;
}
function DailyCapEditor({ current, disabled, onSave }: CapProps) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(current));
  if (!editing) {
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => { setVal(String(current)); setEditing(true); }}
      >
        Edit cap
      </Button>
    );
  }
  const n = Math.max(0, Math.floor(Number(val) || 0));
  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        min={0}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="h-8 w-20"
      />
      <Button
        size="sm"
        disabled={disabled || n === current}
        onClick={() => { onSave(n); setEditing(false); }}
      >
        Save
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </div>
  );
}

// ---------- Add dialog ----------------------------------------------------

interface AddDialogProps {
  open: boolean;
  prefill: Partial<typeof TG_QUICK_ADD> | null;
  onClose: () => void;
  onSaved: () => void;
}
function AddSenderDialog({ open, prefill, onClose, onSaved }: AddDialogProps) {
  const [channel, setChannel] = useState<Channel>('tg');
  const [identifier, setIdentifier] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [dailyCap, setDailyCap] = useState(15);
  const [enabled, setEnabled] = useState(false);
  const [evolutionInstance, setEvolutionInstance] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { show: toast } = useToast();

  // Re-seed local state from `prefill` each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setChannel(prefill?.channel ?? 'tg');
    setIdentifier(prefill?.identifier ?? '');
    setDisplayName(prefill?.display_name ?? '');
    setDailyCap(prefill?.daily_cap ?? 15);
    setEnabled(prefill?.enabled ?? false);
    setEvolutionInstance('');
    setNotes('');
    setErr(null);
  }, [open, prefill]);

  async function save() {
    setErr(null);
    setBusy(true);
    try {
      await api('/api/admin/outreach/sender-accounts', {
        method: 'POST',
        json: {
          channel,
          identifier: identifier.trim(),
          ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
          daily_cap: dailyCap,
          enabled,
          ...(channel === 'wa' && evolutionInstance.trim()
            ? { evolution_instance: evolutionInstance.trim() }
            : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      });
      toast('Sender account saved', 'success');
      onSaved();
      onClose();
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.body as { error?: unknown })?.error
            ? `Error ${e.status}: ${JSON.stringify((e.body as { error: unknown }).error).slice(0, 200)}`
            : `Error ${e.status}`
        : 'Save failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  const canSave = identifier.trim().length > 0 && (channel !== 'wa' || evolutionInstance.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add sender account</DialogTitle>
          <DialogDescription>
            Each enabled account contributes to the daily send capacity.
            Approved drafts queue up across all enabled senders.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid gap-1.5">
            <Label>Channel</Label>
            <Select value={channel} onValueChange={(v) => setChannel(v as Channel)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tg">Telegram (via godrop-tg-bridge)</SelectItem>
                <SelectItem value="wa">WhatsApp (via Evolution API)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label>Identifier</Label>
            <Input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={channel === 'tg' ? 'numeric tg_user_id (e.g. 8572914098)' : 'phone (e.g. +6580000000)'}
              autoComplete="off"
            />
            <p className="text-[11px] text-text-muted">
              {channel === 'tg'
                ? "Numeric tg_user_id of the userbot account. Must match the account authenticated in the bridge's tg.session — otherwise sends fail."
                : "E.164 phone of the outreach WA Business number (e.g. +6581234567). The Evolution instance below must be wired to this number."}
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label>Display name (optional)</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. GoDrop outreach +6581161183"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Daily cap</Label>
              <Input
                type="number"
                min={0}
                value={dailyCap}
                onChange={(e) => setDailyCap(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
              />
              <p className="text-[11px] text-text-muted">
                Max DMs this account will send per day.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label>Enabled</Label>
              <label className="mt-2 flex items-center gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Send through this account
              </label>
            </div>
          </div>

          {channel === 'wa' && (
            <div className="grid gap-1.5">
              <Label>Evolution instance</Label>
              <Input
                value={evolutionInstance}
                onChange={(e) => setEvolutionInstance(e.target.value)}
                placeholder="e.g. godrop-outreach-1"
              />
              <p className="text-[11px] text-text-muted">
                The Evolution API instance name for this number. Must be
                separate from the OTP + support instances (invariant 3
                carve-out). Required for WA.
              </p>
            </div>
          )}

          <div className="grid gap-1.5">
            <Label>Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="anything load-bearing for ops"
            />
          </div>

          {err && (
            <p className="rounded-md border border-error/40 bg-error-tint px-3 py-2 text-xs text-error-on-tint">
              {err}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !canSave}>
            Save sender
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
